import { setProgressbarValue } from '../react/components/progressbar';
import { id_progressbar } from '../react/constants/html-ids';
import { clamp } from '../common/maths';
import { ContactId, DocsId /* UspId */ } from '../react/constants/infobox-ids';
import { evt_StartSceneLoaded } from '../react/constants/custom-event-names';
import { createTimelineBuilder } from '../animation-timeline/timeline-builder';
import { createTimer } from '../animation-timeline/timer';
import {
  createSetKeyframeFn,
  setKeyframe,
} from '../animation/v3d-animation-interface';
import { setHotspotVisibility } from '../v3d-procedures/set-hotspot-visibility';
// import { toggleContinuousAnimation } from '../v3d-procedures/toggle-continuous-animation';
import { cameraProps, CameraMode } from './camera-state';
import { timeline, MODE_CONTINUOUS, MODE_SINGLE_STEP } from './timeline-state';
import {
  changeMode,
  changePerspectiveByIndex,
} from '../camera/camera-interface';
import { useStore, CssMode } from './ui-store';

const open = (status: boolean) =>
  status ? CssMode.OPEN_IMMEDIATELY : CssMode.OPEN_SLOWLY;
const close = (status: boolean) =>
  status ? CssMode.CLOSE_IMMEDIATELY : CssMode.CLOSE_SLOWLY;
const evaluateInfoWindowState = (
  bttnState: boolean,
  infoBttnState: boolean
) => {
  if (bttnState) {
    return CssMode.CLOSE_IMMEDIATELY;
  } else if (infoBttnState) {
    return CssMode.OPEN_IMMEDIATELY;
  }
  return CssMode.CLOSE_IMMEDIATELY;
};

let callback: Default.Callback | null;

let playStateBeforeWindingStarts = false;

let sequence: Array<any> = [];

export const getSequence = () => sequence;

const changeCameraPerspective = (index: number, mode: CameraMode) => {
  changeMode(mode);
  changePerspectiveByIndex(index, mode, timeline.speed < 0);
};

const {
  openVideoDialog,
  expandMenu,
  shrinkMenu,
  setActiveLanguage,
  setInfoWindowStyle,
  setSettingsWindowStyle,
  setHelpWindowStyle,
  setLangWindowStyle,
  openHelpMenu,
  closeHelpMenu,
  openSettingsMenu,
  closeSettingsMenu,
  openInfoMenu,
  closeInfoMenu,
  openLanguageMenu,
  closeLanguageMenu,
  openSidebarInPortraitMode,
  closeSidebarInPortaitMode,
  // turnPowerOn, // not in use in this project
  // turnPowerOff, // not in use in this project
  showHotspots,
  hideHotspots,
  pinCamera,
  freeCamera,
  selectContinuousMode,
  selectSingleStepMode,
  showPlaybar,
  hidePlaybar,
  setPageId,
  setActivePageIndex,
  setLastPageIndex,
  openToc,
  closeToc,
  showPauseButton,
  showPlayButton,
  showDefaultIcon,
  showWindForwardIcon,
  showWindBackwardIcon,
} = useStore.getState();

let allowPerspectiveUpdates = true;

const createFn2UpdateTimeAndSetPage = (idx: number) => (tp: number) => {
  timeline.currentTime = tp;
  setProgressbarValue(id_progressbar, timeline.progress);

  const { pageIndex } = useStore.getState();

  if (idx !== pageIndex) {
    setLastPageIndex(pageIndex);
    setActivePageIndex(idx);
    setPageId(idx.toString());
    if (allowPerspectiveUpdates) {
      // console.info('changePerspectiveByIndex');
      changePerspectiveByIndex(idx, cameraProps.mode, timeline.speed < 0);
    }
  }
};

const entryPoints: Default.Str2NumMap = {};

const timer = createTimer();

const { createStaticGroup, createAnimationGroup } = createTimelineBuilder({
  timer,
  entryPoints,
  createFn2UpdateTimeAndSetPage,
  createFn2ControlAnimation: createSetKeyframeFn,
});

sequence = [
  ...createStaticGroup(0, 6),
  ...createStaticGroup(1, 6),
  ...createStaticGroup(2, 6),
  ...createStaticGroup(3, 6),
  ...createAnimationGroup(4, 5, 215, 30),
  ...createAnimationGroup(5, 215, 250, 30),
  ...createAnimationGroup(6, 250, 420, 30),
  ...createAnimationGroup(7, 420, 455, 30),
  ...createAnimationGroup(8, 455, 497, 30),
  ...createAnimationGroup(9, 497, 545, 30),
  ...createAnimationGroup(10, 545, 885, 30),
  ...createAnimationGroup(11, 885, 940, 30),
  ...createAnimationGroup(12, 940, 1100, 30),
  ...createAnimationGroup(13, 1100, 1210, 30),
  ...createAnimationGroup(14, 1210, 1305, 30),
  ...createAnimationGroup(15, 1305, 1605, 30),
  ...createAnimationGroup(16, 1605, 1703, 30),
  ...createAnimationGroup(17, 1703, 1767, 30),
  ...createAnimationGroup(18, 1767, 1993, 30),
  ...createAnimationGroup(19, 1993, 2103, 30),
  ...createAnimationGroup(20, 2103, 2420, 30),
];

timeline.targetTime = timer.get();
timeline.totalTime = timer.get();

const entryList = Object.values(entryPoints);
const lastEntryIndex = entryList.length - 1;

const startWindingOperation = (
  from: number,
  to: number,
  speed: number,
  cb: Default.Callback | null = null
) => {
  const windForward = to > from;

  timeline.isPlaying = true;
  timeline.speed = windForward ? speed : -speed;
  timeline.targetTime = to;

  closeToc(); // KPAPMP-223 TOC shall be closed when page is selected
  showPlayButton();
  if (windForward) {
    showWindForwardIcon();
  } else {
    showWindBackwardIcon();
  }
  callback = cb;
};

const onGovieEndReached = () => {
  timeline.reset = true;
  timeline.currentTime = 0;
  timeline.progress = 0;
  cameraProps.tweenCurrentTime = 0;
  cameraProps.tweenProgress = 0;
  setKeyframe(0);
  setProgressbarValue(id_progressbar, 0);
  sequence.forEach((el) => {
    if (el && el.onTimeChange) el.onTimeChange(0);
  });
  timeline.reset = false;
};

// wird auch ausgelöst, wenn man im Schritt für Schritt Modus ist und am Ende eines Schrittes angelangt ist.
const finishWindingOperation = () => {
  if (timeline.targetTime === timeline.totalTime) {
    onGovieEndReached();
  } else {
    timeline.isPlaying = false;
    timeline.speed = timeline.defaultSpeed;
    timeline.targetTime = timeline.totalTime;

    showPlayButton();
    showDefaultIcon();

    if (callback) {
      callback();
      callback = null;
    }

    allowPerspectiveUpdates = true;
  }
};

const evalSpeed = (targetTime: number, seconds: number) => {
  const { currentTime } = timeline;
  return Math.abs(currentTime - targetTime) / seconds;
};

/* // Not used in this project 
export const createSubstate = (eventNames?: Array<string>) => {
  eventNames?.forEach((key) => {
    const fn = state.events[key];
    if (!fn) {
      console.warn(`Event key unknown ${key}`);
      return;
    }
    fn();
  });
}; */

const onUserInteraction = () => {
  const { hotspotBttnActive, cameraBttnActive, playPause } =
    useStore.getState();

  if (
    playPause !== false ||
    hotspotBttnActive !== true ||
    cameraBttnActive !== false
  ) {
    // showPlayButton();
    setHotspotVisibility(true);
    showHotspots();
    freeCamera();
    // timeline.isPlaying = false;
    changeMode(CameraMode.ControlledByUser);
  }
};

/* // Not used in this project
const onHotspotSelected = (id: string) => {
  const { hotspots } = state;

  const eventNames = hotspots[id];
  if (!eventNames) {
    console.warn(`No events connected with hotspot: ${id}`);
    return;
  }

  createSubstate(eventNames);
}; */

const onInfoBttnClick = () => {
  const { infoBttnActive } = useStore.getState();
  const newVal1 = !infoBttnActive;
  shrinkMenu();
  closeHelpMenu(); // settings, help and lang are already closed
  closeSettingsMenu(); // settings, help and lang are already closed
  if (newVal1) {
    openInfoMenu();
    setInfoWindowStyle(CssMode.OPEN_SLOWLY);
  } else {
    closeInfoMenu();
    setInfoWindowStyle(CssMode.CLOSE_SLOWLY);
  }
  closeLanguageMenu(); // settings, help and lang are already closed
};

const onSettingsBttnClick = () => {
  const {
    menuOccupiesEntireSpace,
    helpBttnActive,
    settingsBttnActive,
    infoBttnActive,
    langBttnActive,
    moreBttnActive,
  } = useStore.getState();
  const nextState = !settingsBttnActive;
  const cssState = nextState
    ? open(infoBttnActive || helpBttnActive || langBttnActive)
    : close(infoBttnActive);
  const cssInfo = evaluateInfoWindowState(nextState, infoBttnActive);

  timeline.isPlaying = false;

  showPlayButton();

  setInfoWindowStyle(cssInfo);
  setSettingsWindowStyle(cssState);
  setHelpWindowStyle(CssMode.CLOSE_IMMEDIATELY);
  setLangWindowStyle(CssMode.CLOSE_IMMEDIATELY);
  if (menuOccupiesEntireSpace) {
    expandMenu();
  } else {
    shrinkMenu();
  }

  if (moreBttnActive && !nextState) {
    openSidebarInPortraitMode();
  } else {
    closeSidebarInPortaitMode();
  }

  closeHelpMenu();
  if (nextState) {
    openSettingsMenu();
  } else {
    closeSettingsMenu();
  }
  closeLanguageMenu();
};

const onHelpBttnClick = () => {
  const {
    menuOccupiesEntireSpace,
    helpBttnActive,
    settingsBttnActive,
    infoBttnActive,
    langBttnActive,
    moreBttnActive,
  } = useStore.getState();
  const nextState = !helpBttnActive;
  const cssState = nextState
    ? open(infoBttnActive || settingsBttnActive || langBttnActive)
    : close(infoBttnActive);
  const cssInfo = evaluateInfoWindowState(nextState, infoBttnActive);

  timeline.isPlaying = false;

  showPlayButton();

  setInfoWindowStyle(cssInfo);
  setSettingsWindowStyle(CssMode.CLOSE_IMMEDIATELY);
  setHelpWindowStyle(cssState);
  setLangWindowStyle(CssMode.CLOSE_IMMEDIATELY);

  if (menuOccupiesEntireSpace) {
    expandMenu();
  } else {
    shrinkMenu();
  }

  if (moreBttnActive && !nextState) {
    openSidebarInPortraitMode();
  } else {
    closeSidebarInPortaitMode();
  }

  if (nextState) {
    openHelpMenu();
  } else {
    closeHelpMenu();
  }
  closeSettingsMenu();
  closeLanguageMenu();
};

const onLangBttnClick = () => {
  const {
    menuOccupiesEntireSpace,
    helpBttnActive,
    settingsBttnActive,
    infoBttnActive,
    langBttnActive,
    moreBttnActive,
  } = useStore.getState();
  const nextState = !langBttnActive;
  const cssState = nextState
    ? open(infoBttnActive || settingsBttnActive || helpBttnActive)
    : close(infoBttnActive);
  const cssInfo = evaluateInfoWindowState(nextState, infoBttnActive);

  timeline.isPlaying = false;

  showPlayButton();

  setInfoWindowStyle(cssInfo);
  setSettingsWindowStyle(CssMode.CLOSE_IMMEDIATELY);
  setHelpWindowStyle(CssMode.CLOSE_IMMEDIATELY);
  setLangWindowStyle(cssState);

  if (menuOccupiesEntireSpace) {
    expandMenu();
  } else {
    shrinkMenu();
  }

  if (moreBttnActive && !nextState) {
    openSidebarInPortraitMode();
  } else {
    closeSidebarInPortaitMode();
  }

  closeHelpMenu();
  closeSettingsMenu();
  if (nextState) {
    openLanguageMenu();
  } else {
    closeLanguageMenu();
  }
};

/* const onUspBttnClick = () => {
  const { infoBttnActive, pageId, pageIndex } = state.ui;
  const nextId = pageId === UspId ? pageIndex.toString() : UspId;
  update({
    ui: {
      pageId: nextId,
      infoBttnActive: true,
      infoWindowCssState: open(infoBttnActive),
    },
  });
}; */

const onContactBttnClick = () => {
  const { infoBttnActive, pageIndex, pageId } = useStore.getState();
  const nextId = pageId === ContactId ? pageIndex.toString() : ContactId;
  const cssInfo = open(infoBttnActive);

  setInfoWindowStyle(cssInfo);

  openInfoMenu();
  setPageId(nextId);
};

const onDocsBttnClick = () => {
  const { infoBttnActive, pageIndex, pageId } = useStore.getState();
  const nextId = pageId === DocsId ? pageIndex.toString() : DocsId;
  const cssInfo = open(infoBttnActive);

  setInfoWindowStyle(cssInfo);
  openInfoMenu();
  setPageId(nextId);
};

const onMoreBttnClick = () => {
  const { moreBttnActive } = useStore.getState();

  shrinkMenu();

  if (!moreBttnActive) {
    openSidebarInPortraitMode();
  } else {
    closeSidebarInPortaitMode();
  }

  closeHelpMenu();
  closeSettingsMenu();
  closeLanguageMenu();
};

/* const onPowerBttnClick = () => {
  const newState = !state.ui.powerBttnActive;
  update({
    ui: {
      powerBttnActive: newState,
    },
  });
  const objectName = 'Torus'; // TODO
  toggleContinuousAnimation(objectName, newState);
}; */

const onHotspotBttnClick = () => {
  const { hotspotBttnActive } = useStore.getState();
  const newState = !hotspotBttnActive;

  setHotspotVisibility(newState);
  if (newState) {
    showHotspots();
  } else {
    hideHotspots();
  }
};

const onGovieModeBttnClick = () => {
  const { govieModeBttnActive } = useStore.getState();
  const nextMode = !govieModeBttnActive;

  timeline.isPlaying = false;
  timeline.mode = nextMode ? MODE_CONTINUOUS : MODE_SINGLE_STEP;

  showPlayButton();

  if (nextMode) {
    selectContinuousMode();
  } else {
    selectSingleStepMode();
  }
};

const onCameraBttnClick = () => {
  const { pageIndex, cameraBttnActive } = useStore.getState();
  const newState = !cameraBttnActive;

  timeline.isPlaying = false;
  showPlayButton();

  if (newState) {
    pinCamera();
    const newVal5 = clamp(pageIndex, 0, lastEntryIndex);

    // TODO: evaluiere genauen Zeitpunkt und springe genau an diese Stelle, statt Einsprungpunkt zu nehmen
    changeCameraPerspective(newVal5, CameraMode.ControlledByMainLoop);
  } else {
    freeCamera();
  }
};

const onGovieMenuBttnClick = () => {
  const { playbarBttnActive } = useStore.getState();
  if (!playbarBttnActive) {
    showPlaybar();
  } else {
    hidePlaybar();
  }
};

const getNextTargetTime = () => {
  const { pageIndex } = useStore.getState();
  const newVal6 = clamp(pageIndex + 1, 0, lastEntryIndex);
  return entryList[newVal6];
};

const autoPlayOnWindingComplete = (targetTime: number) => {
  timeline.isPlaying = true;
  timeline.targetTime = targetTime;

  showPauseButton();
};

const onBackwardBttnClick = () => {
  const { pageIndex } = useStore.getState();
  const { currentTime, windingSpeedOnSinglePageChange } = timeline;

  let trgIndex = pageIndex;
  if (entryList.includes(currentTime)) {
    trgIndex -= 1;
  }

  const newVal4 = clamp(trgIndex, 0, lastEntryIndex);
  const targetTime = entryList[newVal4];

  const speed = evalSpeed(targetTime, windingSpeedOnSinglePageChange);

  // TODO

  startWindingOperation(currentTime, targetTime, speed, () =>
    autoPlayOnWindingComplete(getNextTargetTime())
  );
};

const onBackwardBttnDoubleClick = () => {
  const { pageIndex, cameraBttnActive } = useStore.getState();
  const { currentTime, windingSpeedOnSinglePageChange } = timeline;

  const newVal4 = clamp(pageIndex - 1, 0, lastEntryIndex);

  const targetTime = entryList[newVal4];

  const speed = evalSpeed(targetTime, windingSpeedOnSinglePageChange);

  if (cameraBttnActive) {
    const lastState = allowPerspectiveUpdates;
    allowPerspectiveUpdates = false;

    changeCameraPerspective(newVal4, CameraMode.ControlledByMainLoop);
    startWindingOperation(currentTime, targetTime, speed, () => {
      cameraProps.mode = CameraMode.ControlledByTimeline;
      allowPerspectiveUpdates = lastState;
    });
  } else {
    startWindingOperation(currentTime, targetTime, speed);
  }
};

const onFastBackwardBttnPressed = () => {
  const { cameraBttnActive, playPause } = useStore.getState();
  playStateBeforeWindingStarts = playPause;

  timeline.isPlaying = true;
  timeline.speed = timeline.fastBackwardPace;
  timeline.targetTime = 0;

  if (cameraBttnActive) {
    changeMode(CameraMode.ControlledByTimeline);
  } else {
    changeMode(CameraMode.ControlledByUser);
  }

  showWindBackwardIcon();
};

const onFastBackwardBttnReleased = () => {
  timeline.isPlaying = playStateBeforeWindingStarts;
  timeline.speed = timeline.defaultSpeed;
  timeline.targetTime = timeline.totalTime;

  // TODO

  if (playStateBeforeWindingStarts) {
    showPauseButton();
  } else {
    showPlayButton();
  }
  showDefaultIcon();
};

const onPlayPauseBttnClick = () => {
  const { cameraBttnActive, playPause } = useStore.getState();
  const { mode, totalTime } = timeline;

  const isPlaying = !playPause;

  let targetTime = totalTime;
  if (mode === MODE_SINGLE_STEP) {
    targetTime = getNextTargetTime();
  }

  timeline.isPlaying = isPlaying;
  timeline.targetTime = targetTime;

  if (isPlaying) {
    timeline.speed = timeline.defaultSpeed;
    showPauseButton();
  } else {
    showPlayButton();
  }
  setHotspotVisibility(!isPlaying);
  if (isPlaying) {
    hideHotspots();
  } else {
    showHotspots();
  }

  // TODO
  if (isPlaying) {
    if (cameraBttnActive) {
      changeMode(CameraMode.ControlledByTimeline);
    }
  } else {
    changeMode(CameraMode.ControlledByUser);
  }
};

const onStopBttnClick = () => {
  // TODO momentan nicht in Gebrauch!
  const { currentTime } = timeline;

  changeCameraPerspective(0, CameraMode.ControlledByMainLoop);

  startWindingOperation(currentTime, 0, currentTime); // TODO evalSpeed ?
  setHotspotVisibility(true);
};

const onForwardBttnClick = () => {
  const { pageIndex, cameraBttnActive } = useStore.getState();
  const { currentTime, windingSpeedOnSinglePageChange } = timeline;
  const newVal5 = clamp(pageIndex + 1, 0, lastEntryIndex);

  const targetTime = entryList[newVal5];

  const speed = evalSpeed(targetTime, windingSpeedOnSinglePageChange);

  if (cameraBttnActive) {
    const lastState = allowPerspectiveUpdates;
    allowPerspectiveUpdates = false;
    changeCameraPerspective(newVal5, CameraMode.ControlledByMainLoop);
    startWindingOperation(currentTime, targetTime, speed, () => {
      cameraProps.mode = CameraMode.ControlledByTimeline;
      allowPerspectiveUpdates = lastState;
    });
  } else {
    startWindingOperation(currentTime, targetTime, speed);
  }
};

const onFastForwardBttnPressed = () => {
  const { cameraBttnActive, playPause } = useStore.getState();
  playStateBeforeWindingStarts = playPause;

  timeline.isPlaying = true;
  timeline.speed = timeline.fastForwardPace;
  timeline.targetTime = timeline.totalTime;

  if (cameraBttnActive) {
    changeMode(CameraMode.ControlledByTimeline);
  } else {
    changeMode(CameraMode.ControlledByUser);
  }

  showWindForwardIcon();
};

const onFastForwardBttnReleased = () => {
  timeline.isPlaying = playStateBeforeWindingStarts;
  timeline.speed = timeline.defaultSpeed;
  timeline.targetTime = timeline.totalTime;

  // TODO

  if (playStateBeforeWindingStarts) {
    showPauseButton();
  } else {
    showPlayButton();
  }

  showDefaultIcon();
};

const onTocButtonClick = () => {
  const { tocOpen } = useStore.getState();
  timeline.isPlaying = false;
  showPlayButton();

  if (!tocOpen) {
    openToc();
  } else {
    closeToc();
  }
};

const onPageSelected = (index: number) => {
  const { cameraBttnActive } = useStore.getState();
  const { currentTime, windingSpeedOnMultiPageChange } = timeline;
  const newVal6 = clamp(index, 0, lastEntryIndex);

  const targetTime = entryList[newVal6];

  const speed = evalSpeed(targetTime, windingSpeedOnMultiPageChange);

  const { mode } = cameraProps;

  if (cameraBttnActive) {
    changeCameraPerspective(newVal6, CameraMode.ControlledByMainLoop);
    startWindingOperation(currentTime, targetTime, speed, () => {
      cameraProps.mode = mode;
    });
    allowPerspectiveUpdates = false;
  } else {
    startWindingOperation(currentTime, targetTime, speed);
  }
};

const onLanguageChange = (activeLanguage: Localisation.Data) => {
  const { infoBttnActive } = useStore.getState();
  const cssInfo = evaluateInfoWindowState(false, infoBttnActive);
  const cssLang = close(infoBttnActive);

  setActiveLanguage(activeLanguage);

  setInfoWindowStyle(cssInfo);
  setLangWindowStyle(cssLang);

  closeLanguageMenu();
};

const onMarkdownElementClick = (htmlId: string) => {
  const { pageId, activeLanguage } = useStore.getState();
  if (!htmlId) {
    console.warn(
      `An HTML element without an ID was clicked. Please observe page: '${pageId}'`
    );
    return;
  }

  const { dict, iso } = activeLanguage;
  const videoUri = dict[`**${htmlId}`];
  if (videoUri) {
    openVideoDialog(videoUri);
  } else {
    console.warn(
      `No video dialog will be opened, because the current language '${iso}' does not contain the ID: **'${htmlId}' of the clicked HTML element`
    );
  }
};

export const events = {
  finishWindingOperation,
  onUserInteraction,
  // onHotspotSelected,
  onInfoBttnClick,
  onSettingsBttnClick,
  onHelpBttnClick,
  onLangBttnClick,
  // onUspBttnClick,
  onContactBttnClick,
  onDocsBttnClick,
  onMoreBttnClick,
  // onPowerBttnClick,
  onHotspotBttnClick,
  onGovieModeBttnClick,
  onCameraBttnClick,
  onGovieMenuBttnClick,
  onBackwardBttnClick,
  onBackwardBttnDoubleClick,
  onFastBackwardBttnPressed,
  onFastBackwardBttnReleased,
  onPlayPauseBttnClick,
  onStopBttnClick,
  onForwardBttnClick,
  onFastForwardBttnPressed,
  onFastForwardBttnReleased,
  onTocButtonClick,
  onPageSelected,
  onLanguageChange,
  onMarkdownElementClick,
};

window.addEventListener(evt_StartSceneLoaded, () => {
  changeCameraPerspective(0, CameraMode.ControlledByMainLoop);
});
